import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { Events } from 'ionic-angular';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import { Store } from '@ngrx/store';

import { ApiProvider, AuthData, EmptySubscriber } from '../api/api';
import { AppState } from '../../state/state';
import { CollectionActions } from '../../state/actions';
import { FillerShiftModel } from '../../models/filler-shift';
import { SessionProvider } from '../session/session';
import { UserModel, UserUpdateParams } from '../../models/user';

export enum UserEvent {
  UserDidLogout = 'userDidLogout',
  UserDidLogin = 'userDidLogin',
}

@Injectable()
export class UserProvider {
  // Session Keys
  private static readonly USER_DATA_KEY = 'current_user';
  private static readonly AUTH_DATA_KEY = 'auth';

  readonly currentUser$? = new BehaviorSubject<UserModel>(null);
  readonly isLoggedIn$? = new BehaviorSubject(null);
  currentFillerShift$: BehaviorSubject<FillerShiftModel> = new BehaviorSubject<
    FillerShiftModel
  >(null);

  constructor(
    private api: ApiProvider,
    private session: SessionProvider,
    private store: Store<AppState>,
    private collectionActions: CollectionActions,
    private events: Events
  ) {
    this.load();
  }

  private load() {
    this.session.get(UserProvider.USER_DATA_KEY).then(data => {
      let user = data ? new UserModel(data) : null;
      this.currentUser$.next(user);
    });
    this.session.get(UserProvider.AUTH_DATA_KEY).then(data => {
      this.api.authData = data;
      if (data) {
        this.isLoggedIn$.next(data != null);
        this.getUser();
      }
    });
  }

  // User
  login(phone: string, pin: string) {
    var response = this.api
      .call({
        url: '/sessions',
        method: 'POST',
        params: { phone: phone, pin: pin },
      })
      .flatMap(data => {
        return Observable.fromPromise(this.session.clear()).flatMap(() => {
          return this.setAuthData(data);
        });
      });
    response.subscribe(
      () => {
        this.getUser();
        this.isLoggedIn$.next(true);
        this.events.publish(UserEvent.UserDidLogin);
      },
      error => {
        console.log('login error', error);
      }
    );
    return response;
  }

  sendPin(phone: string) {
    return this.api.call({
      url: '/pin_send',
      method: 'POST',
      body: { phone: phone },
    });
  }

  loginWithAuthKey(auth_key: string, user_uid: string) {
    return this.setAuthData({ auth_key: auth_key, user_uid: user_uid }).then(
      () => {
        this.getUser();
        this.isLoggedIn$.next(true);
        this.events.publish(UserEvent.UserDidLogin);
      },
      error => {
        console.log('error logging in', error);
      }
    );
  }

  getUser() {
    let response = this.api
      .call({
        url: `/users/${ApiProvider.USER_UID}`,
        method: 'GET',
      })
      .map(result => new UserModel(result));
    response
      .do(data => {
        this.currentUser$.next(data);
        this.session.set(UserProvider.USER_DATA_KEY, data.json);
      })
      .subscribe(
        res => console.log(res),
        error => {
          if (error.status === 401) {
            this.logout();
            return null;
          }
        }
      );
    return response;
  }

  updateUser(params: UserUpdateParams) {
    let response = this.api
      .call({
        url: `/users/${ApiProvider.USER_UID}`,
        method: 'PATCH',
        body: params,
      })
      .map(result => new UserModel(result));
    response
      .do(data => {
        this.currentUser$.next(data);
        this.session.set(UserProvider.USER_DATA_KEY, data.json);
      })
      .subscribe(new EmptySubscriber());
    return response;
  }

  logout() {
    return this.session
      .clear()
      .then(() => {
        this.currentUser$.next(null);
        this.api.authData = null;
        return this.api
          .call({
            url: '/sessions/current',
            method: 'DELETE',
          })
          .toPromise();
      })
      .then(() => {
        this.isLoggedIn$.next(false);
        this.events.publish(UserEvent.UserDidLogout);
        this.store.dispatch(this.collectionActions.clearAll());
      });
  }

  // shift
  public getCurrentFillerShift() {
    let request = this.api
      .call({
        url: '/filler_shifts?active=true',
        method: 'GET',
        ops: true,
      })
      .do(res => {
        if (res.length) {
          const shift = new FillerShiftModel(res[0]);
          this.currentFillerShift$.next(shift);
        } else {
          this.currentFillerShift$.next(null);
        }
      })
      .subscribe(new EmptySubscriber());
    return request;
  }

  // Private Methods
  private setAuthData(data: AuthData) {
    this.api.authData = data;
    return this.session.set(UserProvider.AUTH_DATA_KEY, data);
  }
}



// WEBPACK FOOTER //
// ./src/providers/user/user.ts